import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { createBrowserRouter, RouterProvider } from "react-router-dom";
import ErrorPage from "./components/errorPage/errorPage";
import { LocalStorageKeys } from "./enums/localStorageKeys";
import { Features } from "./enums/screenNames";
import FinalSignIn from "./pages/finalSignIn/finalSignIn";
import HomeVersion2 from "./pages/homeVersion2/homeVersion2";
import NewUserAuthentication from "./pages/newUserAuthentication/newUserAuthentication";
import ProtectRoute from "./pages/userAuthentication/protectRoute/protectRoute";
import { RootState } from "./redux/store/store";
import { DefaultRouteConfig } from "./routes/defaultConfig";
import { ExternalWorklistRouteConfig } from "./routes/externalWorklistRouteConfig";
import { LegacyExternalWorklistRouteConfig } from "./routes/legacyExternalWorklistRouteConfig";
import { NycbsPharmaPaRouteConfig } from "./routes/nycbsPharmaRouteConfig";
import { PaRouteConfig } from "./routes/paRouteConfig";
import { PharmaPaRouteConfig } from "./routes/pharmaPaRouteConfig";
import { getItemFromLocalStorage } from "./utils/localStorageHelper";
import { checkAndSetInternalOrganization } from "./utils/organizationHelper";

const App = () => {
  const [viewLegacy, setViewLegacy] = useState(false);

  const determineRouteConfig = (features: [string]) => {
    const viewLegacy = getItemFromLocalStorage(LocalStorageKeys.VIEW_LEGACY);
    setViewLegacy(viewLegacy);
    switch (true) {
      case features.includes(Features.PHARMA_PA):
        return PharmaPaRouteConfig;
      case features.includes(Features.NYCBS_PHARMA_EXTERNAL):
        return NycbsPharmaPaRouteConfig;
      case features.includes(Features.EXTERNAL_WORKLIST) && viewLegacy === true:
        return LegacyExternalWorklistRouteConfig;
      case features.includes(Features.EXTERNAL_WORKLIST):
        return ExternalWorklistRouteConfig;
      default:
        return PaRouteConfig;
    }
  };
  const getInitialRouteConfig = () => {
    const storedFeature = getItemFromLocalStorage(LocalStorageKeys.FEATURES);
    if (storedFeature) {
      return determineRouteConfig(storedFeature);
    } else {
      return DefaultRouteConfig;
    }
  };

  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const [routeConfig, setRouteConfig] = React.useState<any>(
    getInitialRouteConfig,
  );
  const [isOrgSetupComplete, setIsOrgSetupComplete] = useState(false);

  const { data: facilityDetails } = useSelector(
    (state: RootState) => state.facilitySlice,
  );
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!user) {
      setRouteConfig(DefaultRouteConfig);
    }

    const fetchFeature = async () => {
      const storedFeature = getItemFromLocalStorage(LocalStorageKeys.FEATURES);

      if (storedFeature) {
        const newRouteConfig = determineRouteConfig(storedFeature);
        if (
          newRouteConfig === ExternalWorklistRouteConfig &&
          !isOrgSetupComplete
        ) {
          try {
            await checkAndSetInternalOrganization();
            setIsOrgSetupComplete(true);
          } catch (error) {
            console.error("Error setting up internal organization:", error);
          }
        }

        setRouteConfig(newRouteConfig);
        clearTimer();
      } else {
        setTimer(fetchFeature, 500);
      }
    };

    const setTimer = (callback: () => void, delay: number) => {
      clearTimer();
      timerRef.current = setTimeout(callback, delay);
    };

    const clearTimer = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };

    if (user) {
      fetchFeature();
    }

    return () => {
      clearTimer();
    };
  }, [user, facilityDetails, isOrgSetupComplete, viewLegacy]);

  const router = useMemo(
    () =>
      createBrowserRouter([
        {
          path: "/",
          element: (
            <ProtectRoute>
              <HomeVersion2 />
            </ProtectRoute>
          ),
          children: routeConfig,
        },
        { path: "/auth/:email?", element: <NewUserAuthentication /> },
        { path: "/finishSignIn", element: <FinalSignIn /> },
        {
          path: "*",
          element: <ErrorPage />,
        },
      ]),
    [routeConfig],
  );
  return (
    <>
      <RouterProvider router={router} />
      <div id="recaptcha-container"></div>
    </>
  );
};

export default App;
